import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Users, 
  Mail, 
  TrendingUp, 
  Calendar,
  Search,
  Download,
  RefreshCw,
  LogOut,
  Eye,
  Trash2
} from 'lucide-react';
import { railsAPI } from '../services/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState({
    active_members: 0,
    training_sessions: 0,
    tools_covered: 0,
    industry_sectors: 0
  });
  const [members, setMembers] = useState([]);
  const [signups, setSignups] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTab, setSelectedTab] = useState('overview');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsData, membersData, signupsData, emailLogsData] = await Promise.all([
        railsAPI.getStats(),
        railsAPI.getAllMembers(),
        railsAPI.getAllSignups(),
        railsAPI.getEmailLogs()
      ]);

      setStats(statsData);
      setMembers(membersData);
      setSignups(signupsData);
      setEmailLogs(emailLogsData);
    } catch (error) {
      toast.error('Failed to load dashboard data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
    toast.success('Dashboard refreshed');
  };

  const handleExportMembers = () => {
    const csv = [
      ['Name', 'Email', 'Profession', 'Joined At', 'Status'],
      ...members.map(m => [m.name, m.email, m.profession || 'N/A', new Date(m.joined_at).toLocaleDateString(), m.status])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rails-members-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Members exported successfully');
  };

  const handleExportSignups = () => {
    const csv = [
      ['Email', 'Signed Up At', 'Notified'],
      ...signups.map(s => [s.email, new Date(s.signed_up_at).toLocaleDateString(), s.notified ? 'Yes' : 'No'])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rails-session-signups-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Signups exported successfully');
  };

  const handleLogout = () => {
    // For now, just redirect to home
    navigate('/');
    toast.success('Logged out successfully');
  };

  const filteredMembers = members.filter(m => 
    m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (m.profession && m.profession.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const filteredSignups = signups.filter(s => 
    s.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="admin-loading">
        <RefreshCw className="animate-spin" size={48} />
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <div className="admin-header">
        <div className="admin-header-content">
          <div>
            <h1 className="admin-title">RAILS by DX Admin</h1>
            <p className="admin-subtitle">Community Management Dashboard</p>
          </div>
          <div className="admin-actions">
            <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
              Refresh
            </Button>
            <Button variant="outline" onClick={handleLogout}>
              <LogOut size={16} />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="admin-container">
        {/* Stats Overview */}
        <div className="admin-stats-grid">
          <Card>
            <CardHeader className="stats-card-header">
              <Users className="stats-icon" />
              <div>
                <CardDescription>Active Members</CardDescription>
                <CardTitle className="stats-value">{members.length}</CardTitle>
              </div>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader className="stats-card-header">
              <Mail className="stats-icon" />
              <div>
                <CardDescription>Session Signups</CardDescription>
                <CardTitle className="stats-value">{signups.length}</CardTitle>
              </div>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader className="stats-card-header">
              <TrendingUp className="stats-icon" />
              <div>
                <CardDescription>Email Logs</CardDescription>
                <CardTitle className="stats-value">{emailLogs.length}</CardTitle>
              </div>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader className="stats-card-header">
              <Calendar className="stats-icon" />
              <div>
                <CardDescription>Training Sessions</CardDescription>
                <CardTitle className="stats-value">{stats.training_sessions}+</CardTitle>
              </div>
            </CardHeader>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Card className="admin-main-card">
          <Tabs value={selectedTab} onValueChange={setSelectedTab}>
            <CardHeader>
              <div className="tabs-header">
                <TabsList>
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="members">Members ({members.length})</TabsTrigger>
                  <TabsTrigger value="signups">Signups ({signups.length})</TabsTrigger>
                  <TabsTrigger value="emails">Email Logs ({emailLogs.length})</TabsTrigger>
                </TabsList>
              </div>
            </CardHeader>

            <CardContent>
              {/* Search Bar */}
              {(selectedTab === 'members' || selectedTab === 'signups') && (
                <div className="search-bar">
                  <Search size={20} />
                  <Input
                    placeholder={`Search ${selectedTab}...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                </div>
              )}

              {/* Overview Tab */}
              <TabsContent value="overview" className="tab-content">
                <div className="overview-grid">
                  <Card>
                    <CardHeader>
                      <CardTitle>Recent Members</CardTitle>
                      <CardDescription>Last 5 members who joined</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="recent-list">
                        {members.slice(0, 5).map((member) => (
                          <div key={member.id} className="recent-item">
                            <div>
                              <p className="recent-name">{member.name}</p>
                              <p className="recent-email">{member.email}</p>
                            </div>
                            <Badge>{new Date(member.joined_at).toLocaleDateString()}</Badge>
                          </div>
                        ))}
                        {members.length === 0 && (
                          <p className="empty-state">No members yet</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Recent Signups</CardTitle>
                      <CardDescription>Last 5 session signups</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="recent-list">
                        {signups.slice(0, 5).map((signup) => (
                          <div key={signup.id} className="recent-item">
                            <div>
                              <p className="recent-email">{signup.email}</p>
                            </div>
                            <Badge variant={signup.notified ? "default" : "secondary"}>
                              {signup.notified ? 'Notified' : 'Pending'}
                            </Badge>
                          </div>
                        ))}
                        {signups.length === 0 && (
                          <p className="empty-state">No signups yet</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Members Tab */}
              <TabsContent value="members" className="tab-content">
                <div className="table-actions">
                  <p className="table-count">{filteredMembers.length} members</p>
                  <Button onClick={handleExportMembers} variant="outline">
                    <Download size={16} />
                    Export CSV
                  </Button>
                </div>

                <ScrollArea className="table-scroll">
                  <div className="data-table">
                    <div className="table-header">
                      <div>Name</div>
                      <div>Email</div>
                      <div>Profession</div>
                      <div>Joined</div>
                      <div>Status</div>
                    </div>
                    {filteredMembers.map((member) => (
                      <div key={member.id} className="table-row">
                        <div className="table-cell-name">{member.name}</div>
                        <div className="table-cell">{member.email}</div>
                        <div className="table-cell">{member.profession || 'N/A'}</div>
                        <div className="table-cell">{new Date(member.joined_at).toLocaleDateString()}</div>
                        <div className="table-cell">
                          <Badge variant={member.status === 'active' ? 'default' : 'secondary'}>
                            {member.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                    {filteredMembers.length === 0 && (
                      <div className="empty-table">No members found</div>
                    )}
                  </div>
                </ScrollArea>
              </TabsContent>

              {/* Signups Tab */}
              <TabsContent value="signups" className="tab-content">
                <div className="table-actions">
                  <p className="table-count">{filteredSignups.length} signups</p>
                  <Button onClick={handleExportSignups} variant="outline">
                    <Download size={16} />
                    Export CSV
                  </Button>
                </div>

                <ScrollArea className="table-scroll">
                  <div className="data-table">
                    <div className="table-header">
                      <div>Email</div>
                      <div>Signed Up</div>
                      <div>Notified</div>
                    </div>
                    {filteredSignups.map((signup) => (
                      <div key={signup.id} className="table-row">
                        <div className="table-cell-name">{signup.email}</div>
                        <div className="table-cell">{new Date(signup.signed_up_at).toLocaleDateString()}</div>
                        <div className="table-cell">
                          <Badge variant={signup.notified ? 'default' : 'secondary'}>
                            {signup.notified ? 'Yes' : 'No'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                    {filteredSignups.length === 0 && (
                      <div className="empty-table">No signups found</div>
                    )}
                  </div>
                </ScrollArea>
              </TabsContent>

              {/* Email Logs Tab */}
              <TabsContent value="emails" className="tab-content">
                <ScrollArea className="table-scroll">
                  <div className="email-logs">
                    {emailLogs.map((log) => (
                      <Card key={log.id} className="email-log-card">
                        <CardHeader>
                          <div className="email-log-header">
                            <div>
                              <CardTitle className="email-log-title">{log.subject}</CardTitle>
                              <CardDescription>To: {log.recipient_email}</CardDescription>
                            </div>
                            <Badge>{log.email_type}</Badge>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <p className="email-log-preview">{log.body.substring(0, 150)}...</p>
                          <p className="email-log-date">
                            {new Date(log.sent_at).toLocaleString()}
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                    {emailLogs.length === 0 && (
                      <div className="empty-state">No email logs yet</div>
                    )}
                  </div>
                </ScrollArea>
              </TabsContent>
            </CardContent>
          </Tabs>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
